<admin-app-header></admin-app-header>

<div class="container-fluid py-4">
    <div class="row">
        <div class="col-12">
            <h1 class="mb-4">
                <i class="fa-solid fa-building-columns me-2"></i>
                Enterprise Management
            </h1>
            <p class="text-muted mb-4">Set up and manage white-label enterprises, enterprise managers, and run migrations.</p>
        </div>
    </div>

    <div class="row mb-4">
        <div class="col-12">
            <div class="step-nav">
                <button class="step-btn" [class.active]="activeStep === 1" (click)="setStep(1)">
                    <span class="step-number">1</span>
                    <span class="step-label">View Config</span>
                </button>
                <button class="step-btn" [class.active]="activeStep === 2" (click)="setStep(2)">
                    <span class="step-number">2</span>
                    <span class="step-label">Create Enterprises</span>
                </button>
                <button class="step-btn" [class.active]="activeStep === 3" (click)="setStep(3)">
                    <span class="step-number">3</span>
                    <span class="step-label">Run Migrations</span>
                </button>
                <button class="step-btn" [class.active]="activeStep === 4" (click)="setStep(4)">
                    <span class="step-number">4</span>
                    <span class="step-label">Enterprise Managers</span>
                </button>
                <button class="step-btn" [class.active]="activeStep === 5" (click)="setStep(5)">
                    <span class="step-number">5</span>
                    <span class="step-label">Assign Portfolios</span>
                </button>
                <button class="step-btn" [class.active]="activeStep === 6" (click)="setStep(6)">
                    <span class="step-number">6</span>
                    <span class="step-label">Data Integrity</span>
                </button>
            </div>
        </div>
    </div>

    <div class="row" *ngIf="activeStep === 1">
        <div class="col-12">
            <div class="card">
                <div class="card-header">
                    <h5 class="mb-0">
                        <i class="fa-solid fa-code me-2"></i>
                        Configured Enterprises (from code)
                    </h5>
                </div>
                <div class="card-body">
                    <p class="text-muted">These enterprises are configured in <code>src/app/config/enterprise.config.ts</code>. This config controls branding, colors, logos, and email settings.</p>

                    <div class="table-responsive">
                        <table class="table table-striped">
                            <thead>
                                <tr>
                                    <th>Key</th>
                                    <th>Name</th>
                                    <th>Domain</th>
                                    <th>Primary Color</th>
                                    <th>Firestore Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr *ngFor="let config of configuredEnterprises">
                                    <td>
                                        <code>{{ config.key }}</code>
                                    </td>
                                    <td>{{ config.name }}</td>
                                    <td>{{ config.domain }}</td>
                                    <td>
                                        <span class="color-swatch" [style.background-color]="config.primaryColor"></span>
                                        {{ config.primaryColor }}
                                    </td>
                                    <td>
                                        <span *ngIf="isEnterpriseInFirestore(config.key)" class="badge bg-success"> <i class="fa-solid fa-check"></i> Created </span>
                                        <span *ngIf="!isEnterpriseInFirestore(config.key)" class="badge bg-warning text-dark"> <i class="fa-solid fa-exclamation-triangle"></i> Not in Firestore </span>
                                    </td>
                                    <td>
                                        <div class="btn-group">
                                            <a [href]="'/enterprise/' + config.key" target="_blank" class="btn btn-sm btn-outline-primary"> <i class="fa-solid fa-eye"></i> View Homepage </a>
                                            <button *ngIf="!isEnterpriseInFirestore(config.key)" class="btn btn-sm btn-primary" (click)="createEnterpriseInFirestore(config.key)" [disabled]="loading">
                                                <i class="fa-solid fa-plus"></i> Create in Firestore
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="alert alert-info mt-4">
                        <h6><i class="fa-solid fa-info-circle me-2"></i>Config File Locations</h6>
                        <ul class="mb-0">
                            <li><strong>Frontend:</strong> <code>parkupp-angular/src/app/config/enterprise.config.ts</code></li>
                            <li><strong>Backend:</strong> <code>parkupp-functions/functions/src/config/enterprise.config.ts</code></li>
                            <li><strong>Logos:</strong> <code>parkupp-angular/src/assets/whitelabel/enterprise-key/logo.png</code></li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div class="row" *ngIf="activeStep === 2">
        <div class="col-lg-6">
            <div class="card mb-4">
                <div class="card-header">
                    <h5 class="mb-0">
                        <i class="fa-solid fa-database me-2"></i>
                        Enterprises in Firestore
                    </h5>
                </div>
                <div class="card-body">
                    <p class="text-muted">These enterprise records exist in Firestore and can be used for data association.</p>

                    <div class="table-responsive" *ngIf="firestoreEnterprises.length > 0">
                        <table class="table">
                            <thead>
                                <tr>
                                    <th>Key</th>
                                    <th>Name</th>
                                    <th>Domain</th>
                                    <th>Active</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr *ngFor="let ent of firestoreEnterprises">
                                    <td>
                                        <code>{{ ent.$key }}</code>
                                    </td>
                                    <td>{{ ent.name }}</td>
                                    <td>{{ ent.domain }}</td>
                                    <td>
                                        <span *ngIf="ent.active" class="badge bg-success">Active</span>
                                        <span *ngIf="!ent.active" class="badge bg-secondary">Inactive</span>
                                    </td>
                                    <td>
                                        <a [href]="'/enterprise/' + ent.$key" target="_blank" class="btn btn-sm btn-outline-primary"> <i class="fa-solid fa-eye"></i> View Homepage </a>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div *ngIf="firestoreEnterprises.length === 0" class="alert alert-warning">No enterprises found in Firestore. Create one using the form or sync from config.</div>

                    <button class="btn btn-outline-secondary btn-sm" (click)="loadFirestoreEnterprises()"><i class="fa-solid fa-refresh"></i> Refresh</button>
                </div>
            </div>
        </div>

        <div class="col-lg-6">
            <div class="card">
                <div class="card-header">
                    <h5 class="mb-0">
                        <i class="fa-solid fa-plus me-2"></i>
                        Create Custom Enterprise
                    </h5>
                </div>
                <div class="card-body">
                    <p class="text-muted">Create a new enterprise directly in Firestore. You will also need to add the config to the code files.</p>

                    <div class="mb-3">
                        <label class="form-label">Enterprise Key</label>
                        <input type="text" class="form-control" [(ngModel)]="newEnterprise.key" placeholder="e.g., redefine" />
                        <small class="text-muted">Lowercase, no spaces. Used as document ID.</small>
                    </div>

                    <div class="mb-3">
                        <label class="form-label">Enterprise Name</label>
                        <input type="text" class="form-control" [(ngModel)]="newEnterprise.name" placeholder="e.g., Redefine" />
                    </div>

                    <div class="mb-3">
                        <label class="form-label">Domain</label>
                        <input type="text" class="form-control" [(ngModel)]="newEnterprise.domain" placeholder="e.g., parking.redefine.co.za" />
                    </div>

                    <button class="btn btn-primary" (click)="createCustomEnterprise()" [disabled]="loading"><i class="fa-solid fa-plus"></i> Create Enterprise</button>
                </div>
            </div>
        </div>
    </div>

    <div class="row" *ngIf="activeStep === 3">
        <div class="col-lg-8">
            <div class="card mb-4">
                <div class="card-header">
                    <h5 class="mb-0">
                        <i class="fa-solid fa-database me-2"></i>
                        Migrate Existing Data to Enterprise
                    </h5>
                </div>
                <div class="card-body">
                    <p class="text-muted">This migration will assign all existing merchants, parkings, subscriptions, and invoices that do not have an enterprise to the selected enterprise.</p>

                    <div class="alert alert-warning">
                        <i class="fa-solid fa-exclamation-triangle me-2"></i>
                        <strong>Important:</strong> Run this once after first deploying the enterprise feature to assign all existing data to "parkupp" (or your default enterprise).
                    </div>

                    <div class="row">
                        <div class="col-md-6">
                            <h6>Quick Migration</h6>
                            <p class="text-muted small">Click to migrate all unassigned data to an enterprise:</p>

                            <div class="d-grid gap-2">
                                <button *ngFor="let ent of firestoreEnterprises" class="btn btn-outline-primary" (click)="runMigration(ent.$key)" [disabled]="migrationStatus.running">
                                    <i class="fa-solid fa-arrow-right"></i>
                                    Migrate to {{ ent.name }}
                                </button>
                            </div>

                            <div *ngIf="firestoreEnterprises.length === 0" class="alert alert-info mt-2">Create an enterprise first (Step 2).</div>
                        </div>

                        <div class="col-md-6">
                            <h6>Migration Status</h6>
                            <div *ngIf="migrationStatus.running" class="text-center py-4">
                                <div class="spinner-border text-primary" role="status">
                                    <span class="visually-hidden">Loading...</span>
                                </div>
                                <p class="mt-2">Migration in progress...</p>
                            </div>

                            <div *ngIf="migrationStatus.lastRun">
                                <p class="text-muted small">Last run: {{ migrationStatus.lastRun | date: 'medium' }}</p>
                                <ul class="list-unstyled">
                                    <li><i class="fa-solid fa-building text-primary me-2"></i> {{ migrationStatus.merchantsCount }} merchants</li>
                                    <li><i class="fa-solid fa-square-parking text-primary me-2"></i> {{ migrationStatus.parkingsCount }} parkings</li>
                                    <li><i class="fa-solid fa-file-signature text-primary me-2"></i> {{ migrationStatus.subscriptionsCount }} subscriptions</li>
                                    <li><i class="fa-solid fa-file-invoice-dollar text-primary me-2"></i> {{ migrationStatus.invoicesCount }} invoices</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="col-lg-4">
            <div class="card">
                <div class="card-header bg-info text-white">
                    <h6 class="mb-0">
                        <i class="fa-solid fa-info-circle me-2"></i>
                        What the migration does
                    </h6>
                </div>
                <div class="card-body">
                    <ul class="small">
                        <li>Finds all documents without <code>enterpriseRef</code></li>
                        <li>Adds <code>enterpriseRef</code> (DocumentReference)</li>
                        <li>Adds <code>denormEnterpriseKey</code> (string)</li>
                        <li>Processes in batches of 500</li>
                    </ul>
                    <p class="text-muted small mb-0">Safe to run multiple times - only updates documents without an enterprise.</p>
                </div>
            </div>
        </div>
    </div>

    <div class="row" *ngIf="activeStep === 4">
        <div class="col-lg-7">
            <div class="card mb-4">
                <div class="card-header">
                    <h5 class="mb-0">
                        <i class="fa-solid fa-users-cog me-2"></i>
                        Enterprise Managers
                    </h5>
                </div>
                <div class="card-body">
                    <p class="text-muted">Enterprise managers have restricted admin access - they can only see data for their assigned enterprise.</p>

                    <div class="table-responsive" *ngIf="enterpriseManagers.length > 0">
                        <table class="table">
                            <thead>
                                <tr>
                                    <th>User UID</th>
                                    <th>Enterprise</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr *ngFor="let manager of enterpriseManagers">
                                    <td>
                                        <code class="small">{{ manager.uid }}</code>
                                        <button class="btn btn-link btn-sm p-0 ms-1" (click)="copyToClipboard(manager.uid)">
                                            <i class="fa-solid fa-copy"></i>
                                        </button>
                                    </td>
                                    <td>{{ getEnterpriseName(manager.enterpriseKey) }}</td>
                                    <td>
                                        <span *ngIf="manager.active" class="badge bg-success">Active</span>
                                        <span *ngIf="!manager.active" class="badge bg-secondary">Inactive</span>
                                    </td>
                                    <td>
                                        <button class="btn btn-sm" [class.btn-outline-danger]="manager.active" [class.btn-outline-success]="!manager.active" (click)="toggleManagerActive(manager)">
                                            <span *ngIf="manager.active">Deactivate</span>
                                            <span *ngIf="!manager.active">Activate</span>
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div *ngIf="enterpriseManagers.length === 0" class="alert alert-info">No enterprise managers found. Create one using the form.</div>

                    <button class="btn btn-outline-secondary btn-sm" (click)="loadEnterpriseManagers()"><i class="fa-solid fa-refresh"></i> Refresh</button>
                </div>
            </div>
        </div>

        <div class="col-lg-5">
            <div class="card">
                <div class="card-header">
                    <h5 class="mb-0">
                        <i class="fa-solid fa-user-plus me-2"></i>
                        Add Enterprise Manager
                    </h5>
                </div>
                <div class="card-body">
                    <div class="mb-3">
                        <label class="form-label">User UID (Firebase Auth)</label>
                        <input type="text" class="form-control" [(ngModel)]="newManager.uid" placeholder="e.g., abc123xyz..." />
                        <small class="text-muted">Find this in Firebase Console - Authentication - Users</small>
                    </div>

                    <div class="mb-3">
                        <label class="form-label">Enterprise</label>
                        <select class="form-select" [(ngModel)]="newManager.enterpriseKey">
                            <option value="">Select Enterprise</option>
                            <option *ngFor="let ent of firestoreEnterprises" [value]="ent.$key">
                                {{ ent.name }}
                            </option>
                        </select>
                    </div>

                    <button class="btn btn-primary" (click)="createEnterpriseManager()" [disabled]="loading"><i class="fa-solid fa-plus"></i> Add Manager</button>
                </div>
            </div>

            <div class="card mt-3">
                <div class="card-header bg-light">
                    <h6 class="mb-0">
                        <i class="fa-solid fa-question-circle me-2"></i>
                        How to find a User UID
                    </h6>
                </div>
                <div class="card-body small">
                    <ol class="mb-0">
                        <li>Go to Firebase Console</li>
                        <li>Select your project</li>
                        <li>Go to Authentication then Users</li>
                        <li>Find the user and copy their UID</li>
                    </ol>
                </div>
            </div>
        </div>
    </div>

    <div class="row" *ngIf="activeStep === 5">
        <div class="col-lg-6">
            <div class="card">
                <div class="card-header">
                    <h5 class="mb-0">
                        <i class="fa-solid fa-briefcase me-2"></i>
                        Assign Portfolio to Enterprise
                    </h5>
                </div>
                <div class="card-body">
                    <p class="text-muted">Move an existing portfolio (and all their parkings, subscriptions, invoices) to a different enterprise.</p>

                    <div class="mb-3">
                        <label class="form-label">Portfolio</label>
                        <ng-select
                            [(ngModel)]="assignPortfolio.portfolioKey"
                            [items]="portfolios"
                            bindLabel="companyName"
                            bindValue="$key"
                            placeholder="Search by portfolio name..."
                            [searchable]="true"
                            [clearable]="true"
                        >
                            <ng-template ng-option-tmp let-item="item">
                                <div class="d-flex justify-content-between align-items-center">
                                    <span>{{ item.companyName }}</span>
                                    <small class="text-muted" *ngIf="item.denormEnterpriseKey"> <i class="fa-solid fa-building me-1"></i>{{ item.denormEnterpriseKey }} </small>
                                </div>
                            </ng-template>
                        </ng-select>
                        <small class="text-muted">{{ portfolios.length }} portfolios available</small>
                    </div>

                    <div class="mb-3">
                        <label class="form-label">Target Enterprise</label>
                        <select class="form-select" [(ngModel)]="assignPortfolio.enterpriseKey">
                            <option value="">Select Enterprise</option>
                            <option *ngFor="let ent of firestoreEnterprises" [value]="ent.$key">
                                {{ ent.name }}
                            </option>
                        </select>
                    </div>

                    <button class="btn btn-primary" (click)="assignPortfolioToEnterprise()" [disabled]="loading"><i class="fa-solid fa-arrow-right"></i> Assign Portfolio</button>
                </div>
            </div>
        </div>

        <div class="col-lg-6">
            <div class="card">
                <div class="card-header bg-light">
                    <h6 class="mb-0">
                        <i class="fa-solid fa-info-circle me-2"></i>
                        What this does
                    </h6>
                </div>
                <div class="card-body">
                    <p>When you assign a portfolio to an enterprise, the following happens:</p>
                    <ul>
                        <li>The portfolio's enterpriseRef and denormEnterpriseKey are updated</li>
                        <li>All parkings under this portfolio are updated</li>
                        <li>All subscriptions for this portfolio's parkings are updated</li>
                        <li>All invoices for those subscriptions are updated</li>
                    </ul>
                    <p class="text-muted small mb-0">Use this when onboarding a new enterprise client with existing portfolios.</p>
                </div>
            </div>
        </div>
    </div>

    <!-- Step 6: Data Integrity -->
    <div class="row" *ngIf="activeStep === 6">
        <div class="col-12">
            <div class="card mb-4">
                <div class="card-header d-flex justify-content-between align-items-center">
                    <h5 class="mb-0">
                        <i class="fa-solid fa-check-double me-2"></i>
                        Data Integrity Check
                    </h5>
                    <button class="btn btn-primary" (click)="runIntegrityCheck()" [disabled]="integrityLoading">
                        <i class="fa-solid fa-search me-1" *ngIf="!integrityLoading"></i>
                        <i class="fa-solid fa-spinner fa-spin me-1" *ngIf="integrityLoading"></i>
                        {{ integrityLoading ? 'Checking...' : 'Run Integrity Check' }}
                    </button>
                </div>
                <div class="card-body">
                    <p class="text-muted">
                        This tool scans all collections to find records missing the <code>denormEnterpriseKey</code> field, which is required for enterprise-level data filtering and email branding.
                    </p>

                    <!-- Summary Card -->
                    <div
                        *ngIf="integrityLastChecked"
                        class="alert"
                        [ngClass]="{
                            'alert-success': getIntegrityStatus() === 'success',
                            'alert-warning': getIntegrityStatus() === 'warning',
                            'alert-danger': getIntegrityStatus() === 'error'
                        }"
                    >
                        <div class="d-flex justify-content-between align-items-center">
                            <div>
                                <strong *ngIf="getIntegrityStatus() === 'success'"> <i class="fa-solid fa-check-circle me-1"></i> All records have enterprise keys! </strong>
                                <strong *ngIf="getIntegrityStatus() !== 'success'">
                                    <i class="fa-solid fa-exclamation-triangle me-1"></i> {{ getTotalMissing() }} records missing enterprise key
                                </strong>
                                <span class="ms-2 text-muted">({{ getTotalRecords() }} total records checked)</span>
                            </div>
                            <small class="text-muted">Last checked: {{ integrityLastChecked | date: 'medium' }}</small>
                        </div>
                    </div>

                    <!-- Results Table -->
                    <div class="table-responsive" *ngIf="integrityChecks.length > 0">
                        <table class="table table-striped">
                            <thead>
                                <tr>
                                    <th>Collection</th>
                                    <th class="text-center">Total Records</th>
                                    <th class="text-center">Missing Enterprise Key</th>
                                    <th class="text-center">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr *ngFor="let check of integrityChecks">
                                    <td>
                                        <code>{{ check.collection }}</code>
                                        <span class="text-muted ms-2">({{ check.displayName }})</span>
                                    </td>
                                    <td class="text-center">
                                        <span *ngIf="!check.loading && !check.error">{{ check.totalCount }}</span>
                                        <span *ngIf="check.loading" class="text-muted">
                                            <i class="fa-solid fa-spinner fa-spin"></i>
                                        </span>
                                        <span *ngIf="check.error" class="text-danger">Error</span>
                                    </td>
                                    <td class="text-center">
                                        <span *ngIf="!check.loading && !check.error" [class.text-danger]="check.missingCount > 0" [class.fw-bold]="check.missingCount > 0">
                                            {{ check.missingCount }}
                                        </span>
                                        <span *ngIf="check.loading" class="text-muted">
                                            <i class="fa-solid fa-spinner fa-spin"></i>
                                        </span>
                                        <span *ngIf="check.error" class="text-danger" title="{{ check.error }}">
                                            <i class="fa-solid fa-exclamation-circle"></i>
                                        </span>
                                    </td>
                                    <td class="text-center">
                                        <span *ngIf="!check.loading && !check.error && check.missingCount === 0" class="badge bg-success"> <i class="fa-solid fa-check"></i> OK </span>
                                        <span *ngIf="!check.loading && !check.error && check.missingCount > 0" class="badge bg-danger"> <i class="fa-solid fa-exclamation"></i> Needs Migration </span>
                                        <span *ngIf="check.loading" class="badge bg-secondary">Checking...</span>
                                        <span *ngIf="check.error" class="badge bg-warning text-dark" title="{{ check.error }}">Error</span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Empty State -->
                    <div *ngIf="integrityChecks.length === 0" class="text-center py-4">
                        <i class="fa-solid fa-database fa-3x text-muted mb-3"></i>
                        <p class="text-muted">Click "Run Integrity Check" to scan all collections for missing enterprise keys.</p>
                    </div>
                </div>
            </div>

            <!-- Info Card -->
            <div class="card">
                <div class="card-header bg-info text-white">
                    <h6 class="mb-0">
                        <i class="fa-solid fa-info-circle me-2"></i>
                        About Enterprise Keys
                    </h6>
                </div>
                <div class="card-body">
                    <p>The <code>denormEnterpriseKey</code> field is used for:</p>
                    <ul>
                        <li><strong>Data Filtering:</strong> Enterprise managers only see data for their enterprise</li>
                        <li><strong>Email Branding:</strong> Emails use the correct enterprise template and sender</li>
                        <li><strong>Security Rules:</strong> Firestore rules use this field for access control</li>
                    </ul>
                    <p class="mb-0">If records are missing this field, run the migration in <strong>Step 3</strong> to assign them to an enterprise.</p>
                </div>
            </div>
        </div>
    </div>

    <div class="loading-overlay" *ngIf="loading">
        <div class="spinner-border text-light" role="status">
            <span class="visually-hidden">Loading...</span>
        </div>
    </div>
</div>
